// MapStore is the main storage for the map editor. It contains all
// map data.

import { InstructionCounts, InstructionType } from 'app/src/engine/Instruction';
import BetaRobot from 'app/src/engine/movable-items/BetaRobot';
import {
	getTurnsFromOrientation,
	MovableItemLocation,
} from 'app/src/engine/movable-items/MovableItem';
import Robot from 'app/src/engine/movable-items/Robot';
import Star, { StarLocation } from 'app/src/engine/Star';
import { generateRandomEmptyFloorTile } from 'app/src/engine/tiles/EmptyFloorTile';
import Tile from 'app/src/engine/tiles/Tile';
import { cloneLevelDescription, LevelDescription } from 'app/src/LevelDescription';
import GamePlugin from 'app/src/plugins/GamePlugin';
import PubSub from 'app/src/utils/PubSub';

const DEFAULT_MAP_ROWS = 4;
const DEFAULT_MAP_COLS = 6;

function generateInitialTiles(): Tile[][] {
	return new Array(DEFAULT_MAP_ROWS)
		.fill(true)
		.map(() => new Array(DEFAULT_MAP_COLS).fill(true).map(() => generateRandomEmptyFloorTile()));
}

export default class MapStore {
	// MARK: Pubsub
	private _onUpdate = new PubSub<void>();

	// MARK: Properties

	private _title: string;
	private _description: string;

	private _tiles: Tile[][];

	private _stars: StarLocation[];
	private _robot: MovableItemLocation | null;

	private _instructionCounts: InstructionCounts;

	private _plugins: Set<GamePlugin>;

	// MARK: Accessors

	get onUpdate(): PubSub<void> {
		return this._onUpdate;
	}

	public get title(): string {
		return this._title;
	}

	public get description(): string {
		return this._description;
	}

	public get tiles(): Tile[][] {
		return this._tiles;
	}

	public get stars(): StarLocation[] {
		return this._stars;
	}

	public get robot(): MovableItemLocation | null {
		return this._robot;
	}

	public get instructionCounts(): InstructionCounts {
		return this._instructionCounts;
	}

	constructor(plugins: Set<GamePlugin>) {
		this._plugins = plugins;

		this.reset(true);
	}

	public reset(suppressPublishing = false): void {
		this._tiles = generateInitialTiles();
		this._stars = [];
		this._robot = null;
		this._instructionCounts = new Map();
		this._title = 'New Map';
		this._description = 'Enter a description.';

		if (!suppressPublishing) {
			this._onUpdate.publish();
		}
	}

	public buildLevelDescription(): LevelDescription {
		const robots = new Set<Robot>();
		const robotLocations = new Map<Robot, MovableItemLocation>();

		if (this._robot) {
			const robot = new BetaRobot('Robot1');
			robots.add(robot);
			robotLocations.set(robot, this._robot);
		}

		const gamePlugins = new Set<GamePlugin>();

		for (const row of this._tiles) {
			for (const tile of row) {
				const plugin = Array.from(this._plugins).find((p) =>
					p.getListOfProvidedTiles().includes(tile.tileName),
				);

				if (!plugin) {
					throw new Error('Missing game plugin for tile.');
				}

				gamePlugins.add(plugin);
			}
		}

		const levelDescription: LevelDescription = {
			title: this._title,
			description: this._description,
			tiles: this._tiles,
			stars: new Set(this._stars.map((location) => new Star(location))),
			robots,
			robotLocations,
			movableItems: new Set(),
			movableItemLocations: new Map(),
			instructionCounts: new Map(this._instructionCounts),
			gamePlugins,
		};

		return cloneLevelDescription(levelDescription);
	}

	// === Mutators ===

	public setTitle(newTitle: string): void {
		this._title = newTitle;
		this._onUpdate.publish();
	}

	public setDescription(newDescription: string): void {
		this._description = newDescription;
		this._onUpdate.publish();
	}

	public setTiles(tiles: Tile[][]): void {
		// Save tiles.
		this._tiles = tiles;

		// Trigger subscriptions.
		this._onUpdate.publish();
	}

	public setLevelDescription(levelDescription: LevelDescription): void {
		this._title = levelDescription.title;
		this._description = levelDescription.description;

		this._tiles = levelDescription.tiles;

		this._stars = Array.from(levelDescription.stars).map((star) => star.location);

		const robot =
			levelDescription.robots.size === 0 ? null : Array.from(levelDescription.robots)[0];
		this._robot = (robot && levelDescription.robotLocations.get(robot)) || null;

		this._instructionCounts = levelDescription.instructionCounts;

		// Trigger subscriptions.
		this._onUpdate.publish();
	}

	private _moveStarsAndRobots(rows: number, cols: number): void {
		// Shift stars.
		const newStars = this._stars.map((star) => ({ row: star.row + rows, col: star.col + cols }));
		this._stars = newStars;

		// Shift robot.
		if (this._robot) {
			this._robot = {
				row: this._robot.row + rows,
				col: this._robot.col + cols,
				orientation: this._robot.orientation,
				turns: getTurnsFromOrientation(this._robot.orientation),
			};
		}
	}

	public addRowAtTop(): void {
		// Make sure there's some cols.
		const cols = this._tiles.length ? this._tiles[0].length : 0;

		if (!cols) {
			return;
		}

		// Copy the existing tiles and add a row.
		const newRow = new Array(cols).fill('').map(() => generateRandomEmptyFloorTile());
		const newTiles = [newRow, ...this._tiles];

		// Save back the new tiles array.
		this._tiles = newTiles;

		// Move items on the map.
		this._moveStarsAndRobots(1, 0);
	}

	public addRowAtBottom(): void {
		// Make sure there's some cols.
		const cols = this._tiles.length ? this._tiles[0].length : 0;

		if (!cols) {
			return;
		}

		// Copy the existing tiles and add a row.
		const newRow = new Array(cols).fill('').map(() => generateRandomEmptyFloorTile());
		const newTiles = [...this._tiles, newRow];

		// Save back the new tiles array.
		this._tiles = newTiles;
	}

	public addColumnAtLeft(): void {
		const newTiles = this._tiles.map((row) => [generateRandomEmptyFloorTile(), ...row]);
		this._tiles = newTiles;

		this._moveStarsAndRobots(0, 1);
	}

	public addColumnAtRight(): void {
		const newTiles = this._tiles.map((row) => [...row, generateRandomEmptyFloorTile()]);
		this._tiles = newTiles;
	}

	public removeTopRow(): void {
		this._tiles = this._tiles.slice(1);
		this._moveStarsAndRobots(-1, 0);
	}

	public removeBottomRow(): void {
		this._tiles = this._tiles.slice(0, -1);
	}

	public removeLeftColumn(): void {
		this._tiles = this._tiles.map((row) => row.slice(1));
		this._moveStarsAndRobots(0, -1);
	}

	public removeRightColumn(): void {
		this._tiles = this._tiles.map((row) => row.slice(0, -1));
	}

	public updateTile(row: number, col: number, tile: Tile): void {
		// Save the decription in the grid.
		const newTiles = this._tiles.slice();
		const newRow = newTiles[row].slice();
		newRow[col] = tile;
		newTiles[row] = newRow;
		this._tiles = newTiles;

		// Trigger subscriptions.
		this._onUpdate.publish();
	}

	public setStars(stars: StarLocation[]): void {
		this._stars = stars.slice();

		this._onUpdate.publish();
	}

	public addStar(row: number, col: number): void {
		if (this._getIndexOfStar(row, col) !== -1) {
			return;
		}

		const newStars = this._stars.slice();
		newStars.push({ row, col });
		this._stars = newStars;

		this._onUpdate.publish();
	}

	public removeStar(row: number, col: number): void {
		const index = this._getIndexOfStar(row, col);

		if (index === -1) {
			return;
		}

		const newStars = this._stars.slice();
		newStars.splice(index, 1);
		this._stars = newStars;

		this._onUpdate.publish();
	}

	public toggleStar(row: number, col: number): void {
		if (this._getIndexOfStar(row, col) === -1) {
			this.addStar(row, col);
		} else {
			this.removeStar(row, col);
		}
	}

	private _getIndexOfStar(row: number, col: number): number {
		for (let i = 0; i < this._stars.length; i++) {
			if (this._stars[i].row === row && this._stars[i].col === col) {
				return i;
			}
		}

		return -1;
	}

	public setRobot(robot: MovableItemLocation | null): void {
		this._robot = robot;

		this._onUpdate.publish();
	}

	public removeRobot(): void {
		this._robot = null;

		this._onUpdate.publish();
	}

	public setInstructionCount(type: InstructionType, count: number): void {
		const newInstructionCounts = new Map(this._instructionCounts);
		newInstructionCounts.set(type, count);

		this._instructionCounts = newInstructionCounts;

		this._onUpdate.publish();
	}

	public setInstructionCounts(instructionCounts: InstructionCounts): void {
		this._instructionCounts = new Map(instructionCounts);

		this._onUpdate.publish();
	}
}
